import { Box, createStyles, LinearProgress, makeStyles, Modal, Slide, Typography } from '@material-ui/core'
import React, { useCallback, useState, useRef, useEffect } from 'react'
import { SlideForm } from 'src/components/organisms'
import { Accept, useDropzone } from 'react-dropzone'
import { Colors, DynamicMuiIcon, SvgIcon } from 'src/components/atoms'
import { ModalAlert } from '..'
import { ModalPdfFontGuide } from '../ModalPdfFontGuide'
import { useCreateDocument, useUpdateDocumentSlide } from 'src/fixtures/modules/documents/hooks'
import { pushUserActionDataLayer } from 'src/utils/gtm'

const MAX_FILE_SIZE = 104857600
const FILE_TYPE: Accept = { 'application/pdf': ['.pdf'] }

export interface DocumentData {
  file: File
  versionName: string
}

export interface ModalDocumentRegisterProps {
  documentId?: string
  open: boolean
  onClose: () => void
  onCancel: () => void
  onConfirm: () => void
}

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'right'
    },
    dropzone: {
      background: Colors.functional.background.palegray,
      border: `2px dashed ${Colors.background.silver}`,
      borderRadius: 4,
      padding: '60px 24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      transition: 'all 0.3s',
      cursor: 'pointer',
      '&:hover': {
        borderColor: Colors.accent.keyPurple.default,
        background: Colors.selected.background
      }
    },
    warningLinkContainer: {
      color: Colors.accent.keyPurple.default,
      cursor: 'pointer',
      fontWeight: 500,
      display: 'inline-flex',
      alignItems: 'center',
      background: 'none',
      border: 'none',
      padding: 0,
      margin: 0,
      fontFamily: 'inherit',
      fontSize: 'inherit',
      outline: 'none',
      '&:hover $warningLinkText': {
        textDecoration: 'underline'
      },
      '&:focus-visible': {
        outline: `2px solid ${Colors.accent.keyPurple.default}`,
        outlineOffset: '2px',
        borderRadius: '2px'
      }
    },
    warningLinkText: {
      textDecoration: 'none'
    },
    arrowIcon: {
      marginLeft: '4px',
      display: 'inline-flex',
      alignItems: 'center',
      width: '16px',
      height: '16px'
    }
  })
)

export const ModalDocumentRegister = ({
  open,
  onClose,
  onCancel,
  onConfirm,
  documentId
}: ModalDocumentRegisterProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [isOpenPdfGuide, setIsOpenPdfGuide] = useState<boolean>(false)
  // const [versionName, setVersionName] = useState<string>('')
  const [isOpenAlert, setIsOpenAlert] = useState<boolean>(false)
  const { handleCreateDocument, progress, error, setError, isFirstCreatedDocument } = useCreateDocument()
  const {
    handleUpdateDocumentSlide,
    progress: progressUpdate,
    error: updateError,
    setError: setUpdateError
  } = useUpdateDocumentSlide()
  const progressRef = useRef<HTMLDivElement>(null)

  const classes = useStyles()
  const checkCanSubmit = useCallback(() => {
    !file || (file && file.type !== 'application/pdf' && setIsOpenAlert(true))
  }, [file])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      checkCanSubmit()
      setFile(acceptedFiles[0])
      setError(undefined)
      setUpdateError(undefined)
    },
    [checkCanSubmit, setError, setUpdateError]
  )
  // TODO: 正式版以降 verison 管理の時に対応
  // const handleChangeVersionName = useCallback((value: string) => {
  //   setVersionName(value)
  // }, [])

  const { getRootProps, getInputProps, inputRef } = useDropzone({
    onDrop: handleDrop,
    accept: FILE_TYPE,
    maxSize: MAX_FILE_SIZE
  })

  // TODO: hooks に変える(元々の対応を変える)
  const handleConfirm = useCallback(async () => {
    if (progressRef?.current) progressRef.current.style.display = 'block'
    setIsSubmitting(true)
    setTimeout(async () => {
      try {
        if (file && !isSubmitting) {
          if (documentId) {
            await handleUpdateDocumentSlide(documentId, file)
          } else {
            await handleCreateDocument(file)
          }
        }
        onConfirm()
        setFile(null)
      } catch (err) {
        console.error(err)
      } finally {
        setIsSubmitting(false)
        if (progressRef?.current) progressRef.current.style.display = 'none'
        if (inputRef?.current?.value) inputRef.current.value = ''
      }
    }, 0)
  }, [documentId, file, handleCreateDocument, handleUpdateDocumentSlide, inputRef, isSubmitting, onConfirm])

  useEffect(() => {
    if (!open) {
      setIsSubmitting(false)
      setFile(null)
    }
  }, [open])

  useEffect(() => {
    if (isFirstCreatedDocument) {
      pushUserActionDataLayer('document', 'create', 'first_create_document')
    }
  }, [isFirstCreatedDocument])
  return (
    <>
      <Modal open={open} onClose={onClose} className={classes.modal}>
        <Slide direction="left" in={open}>
          <Box width="376px" height={1}>
            <SlideForm
              headerTitle={documentId ? '新しい資料に差し替える' : '資料を登録'}
              onClose={onClose}
              onCancel={onCancel}
              onConfirm={handleConfirm}
              buttonTitle="登録"
              hideSettings
              buttonDisabled={!file}
              isSubmitting={isSubmitting}
            >
              <Box p="24px">
                <Box {...getRootProps()} className={classes.dropzone} color={Colors.base.black} width="100%">
                  <input {...getInputProps()} />
                  {file ? (
                    <>
                      <Box color={Colors.base.middleGray}>
                        <DynamicMuiIcon variant="pdf" color="inherit" size="34px" />
                      </Box>
                      <Box height="20px" />
                      <Typography variant="subtitle1">{file.name}</Typography>
                      <Box width="100%">
                        <div ref={progressRef} style={{ display: 'none' }}>
                          <LinearProgress variant="determinate" value={progress || progressUpdate} />
                        </div>
                      </Box>
                      {(error || updateError) && (
                        <Box
                          display="flex"
                          alignItems="center"
                          color={Colors.accent.keyRed.default}
                          ml="12px"
                          mt="24px"
                        >
                          <DynamicMuiIcon variant="errorOutlineOutlined" size="16px" />
                          <Box mr="6px" />
                          <Typography variant="body2">{error?.message || updateError?.message}</Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>
                      <Box display="flex" justifyContent="center" mb="20px">
                        <SvgIcon variant="uploadFile" color={Colors.accent.keyPurple.default} size="60px" />
                      </Box>
                      <Box color={Colors.accent.keyPurple.default}>
                        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                          PDFファイルをドラッグ
                          <br />
                          またはファイルを選択してください
                        </Typography>
                        <Typography variant="body2" style={{ color: Colors.functional.body2.default, marginTop: 8 }}>
                          （最大100MBまで）
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>

                {/* Guidance area below the dropzone, not clickable */}
                {!file && (
                  <Box mt="20px" width="100%">
                    <Box
                      bgcolor={Colors.label.yellow}
                      border={`2px solid ${Colors.accent.keyYellow.default}`}
                      borderRadius="8px"
                      p="16px"
                      width="100%"
                    >
                      <Box display="flex" alignItems="flex-start" mb="12px">
                        <Box mr="8px" color={Colors.accent.keyYellow.default} flexShrink={0}>
                          <DynamicMuiIcon variant="error" size="24px" />
                        </Box>
                        <Box flex={1}>
                          <Typography
                            variant="body2"
                            style={{ color: Colors.warning.text, fontWeight: 700, lineHeight: 1.5 }}
                          >
                            フォントが埋め込まれていないPDFは正しく表示されない可能性があります
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        style={{ color: Colors.warning.text, lineHeight: 1.6, marginBottom: '12px' }}
                      >
                        特殊なフォントや動画を使用したPDFファイルは、他のユーザーの環境で正常に表示できない場合があります。
                      </Typography>
                      <Box
                        component="button"
                        className={classes.warningLinkContainer}
                        onClick={e => {
                          setIsOpenPdfGuide(true)
                          ;(e.currentTarget as HTMLButtonElement).blur()
                        }}
                      >
                        <Box component="span" className={classes.warningLinkText}>
                          <Typography variant="body2" component="span">
                            対処法を確認する
                          </Typography>
                        </Box>
                        <Box component="span" className={classes.arrowIcon}>
                          <SvgIcon variant="chevronRight" size="16px" color="inherit" />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* {file && (
                  <Box pt="18px" mb="50px">
                    <Typography variant="h6">バージョン名</Typography>
                    <Box height="6px" />
                    <Input
                      name="versionName"
                      placeholder="ファイルの変更点を記載"
                      onChange={e => handleChangeVersionName(e.target.value as string)}
                      fullWidth
                    />
                  </Box>
                )} */}
              </Box>
            </SlideForm>
          </Box>
        </Slide>
      </Modal>
      <ModalPdfFontGuide open={isOpenPdfGuide} onClose={() => setIsOpenPdfGuide(false)} />
      <ModalAlert message="PDFファイルを選択してください" open={isOpenAlert} onClose={() => setIsOpenAlert(false)} />
    </>
  )
}
